import template from './report_company_orgchart.html';
import * as Utils from '../../lib/savanna_utils.js';
import tr from '../../lib/default-translator.js';

class CompanyOrgChartViewModel
{
	constructor (page)
	{
		this.page = page;
		this.isUpdating = false; // Flag to prevent recursive updates
		this.company_details = ko.observable({
			company: ko.observable(),
			company_id: ko.observable()
		});
		this.companies = ko.observableArray();
		this.selected_company = ko.observable();
		this.selected_company.subscribe(async (newValue) => {
			// Prevent recursive updates
			if (this.isUpdating) return;
			
			this.isUpdating = true;
			
			try {
				if (newValue && newValue !== null) {
					await this.page.get_company(newValue);
					await this.page.loadchart();
				} else {
					// Clear company details when no company is selected
					this.company_details().company(null);
					this.company_details().company_id(null);
					this.page.clearChart();
				}
			} catch (error) {
				console.error('Error in selected_company subscription:', error);
				// Reset to null on error to prevent getting stuck
				this.company_details().company(null);
				this.company_details().company_id(null);
				this.selected_company(null); // Ensure selected company is also reset
				this.page.clearChart();
			} finally {
				this.isUpdating = false;
			}
		});
	}

	show_report_help()
	{
		Grape.dialog.open('ManagerSubordinateStructureHelp', { 'employee_id': this.page.bindings.id }, {});
	}

	// Method to safely reset the selected company
	resetCompany()
	{
		if (!this.isUpdating) {
			this.isUpdating = true;
			try {
				// Clear both company details and selected company
				this.company_details().company(null);
				this.company_details().company_id(null);
				this.selected_company(null);
				this.page.clearChart();
			} finally {
				this.isUpdating = false;
			}
		}
	}
}

class CompanyOrgChartPage 
{
	constructor (bindings)
	{
		this.bindings = bindings;
		this.viewModel = new CompanyOrgChartViewModel(this);
		this.chart = null; // Store chart instance
		this.isLoading = false; // Flag to prevent concurrent loading
		this.currentCompanyId = null; // Track current company to detect changes
	}

	async init()
	{
		// check access to page
		if (await Utils.redirectOnAccessDenied('CompanyEmployeesManagerReport'))
			return;

		document.title = tr('Organisational Chart');
		window.current_page_title(tr('Organisational Chart'));

		try {
			let res = await Grape.fetches.getJSON('/api/record', {
				table: 'company',
				limit: 1000000
			});

			if (res && res.records) {
				// Create a new array to avoid reference issues
				const companies = [...res.records];
				companies.unshift({ company_id: null, company: "Please select company" });
				this.viewModel.companies(companies);
			}
		} catch (error) {
			console.error('Error loading companies:', error);
		}
	}

	// Enhanced method to clear the chart completely
	clearChart()
	{
		try {
			// Destroy the existing chart instance completely
			if (this.chart) {
				// If the chart has a destroy method, call it
				if (typeof this.chart.destroy === 'function') {
					this.chart.destroy();
				}
				this.chart = null;
			}

			const chartContainer = document.querySelector('.chart-container');
			if (chartContainer) {
				// Clear all chart content completely
				chartContainer.innerHTML = '';
			}

			// Reset current company tracking
			this.currentCompanyId = null;

		} catch (error) {
			console.error('Error clearing chart:', error);
			this.chart = null;
			this.currentCompanyId = null;
		}
	}

	async loadchart()
	{
		// Prevent concurrent loading
		if (this.isLoading) return;
		
		// Don't load if no company is selected
		if (!this.viewModel.selected_company()) {
			this.clearChart();
			return;
		}

		// Check if we're switching companies
		const newCompanyId = this.viewModel.selected_company();
		const isCompanyChange = this.currentCompanyId !== newCompanyId;
		
		this.isLoading = true;
		
		let chart;
		let employee_tree = [];

		try {
			// Always clear existing chart first, especially on company change
			if (isCompanyChange) {
				this.clearChart();
				// Small delay to ensure DOM is clean
				await new Promise(resolve => setTimeout(resolve, 100));
			}

			// Update current company tracking
			this.currentCompanyId = newCompanyId;

			let my_manager_employee_options = {
				table: 'v_active_manager_employee',
				schema: 'savanna',
				offset: 0,
				limit: 1000000000,
				sortfield: 'surname',
				sortorder: 'ASC',
				filter: [
					{ value: this.viewModel.selected_company(), op: '=', field: 'company_id' },
					{ value: 'TRUE', op: '=', field: 'active' }
				]
			};

			let my_manager_options = {
				table: 'v_manager_department',
				schema: 'savanna',
				offset: 0,
				limit: 1000000000,
				sortfield: 'surname',
				sortorder: 'ASC',
				filter: [
					{ value: true, op: '=', field: 'active' },
					{ value: this.viewModel.selected_company(), op: '=', field: 'company_id' },
					{ value: 'TRUE', op: '=', field: 'active' }
				]
			};

			// Fetch all data concurrently
			const [employeeRes, managersRes, directorRes] = await Promise.all([
				Grape.fetches.getJSON('/api/record', my_manager_employee_options),
				Grape.fetches.getJSON('/api/record', my_manager_options),
				Grape.fetches.getJSON('/api/record', {
					table: 'v_active_employees_department',
					schema: 'savanna',
					offset: 0,
					limit: 1000000000,
					sortfield: 'surname',
					sortorder: 'ASC',
					filter: [
						{ value: this.viewModel.selected_company(), op: '=', field: 'company_id' },
						{ value: '%Director%', op: 'ILIKE', field: 'position' },
						{ value: 'TRUE', op: '=', field: 'active' }
					]
				})
			]);

			// Check if we still have the same company selected (user might have changed it while loading)
			if (!this.viewModel.selected_company() || this.currentCompanyId !== this.viewModel.selected_company()) {
				return;
			}

			let result = employeeRes.records || [];
			let manager_data = managersRes.records || [];
			let director_data = directorRes.records || [];
			let parent_id = "";

			function contains(arr, key, val) {
				return arr.some(item => item[key] === val);
			}

			// Add company root node with unique identifier for this render
			if (parent_id == "") {
				employee_tree.push({
					id: -100,
					parentId: parent_id,
					name: this.viewModel.company_details().company(),
					positionName: "",
					email: "",
					team: "",
					department: "Top level",
					description: "",
					imageUrl: "",
					// Add timestamp and company ID to ensure fresh data
					_timestamp: Date.now(),
					_companyId: this.viewModel.selected_company(),
					_isRoot: true
				});
			}

			// Add managers
			for (let i = 0; i < manager_data.length; i++) {
				if (!contains(employee_tree, "id", manager_data[i].employee_id)) {
					employee_tree.push({
						id: manager_data[i].employee_id,
						parentId: "" + -100 + "",
						name: manager_data[i].firstname + ' ' + manager_data[i].surname,
						positionName: "Manager",
						email: manager_data[i].email,
						team: "",
						department: manager_data[i].department,
						description: "",
						imageUrl: "",
						_companyId: this.viewModel.selected_company()
					});
				}
			}

			// Add additional managers from employee results
			result.forEach(item1 => {
				if (item1.company_id === this.viewModel.selected_company() && 
					!employee_tree.some(item2 => item2.id === item1.manager_employee_id)) {
					employee_tree.push({
						id: item1.manager_employee_id,
						parentId: "" + -100 + "",
						name: item1.manager,
						positionName: "Manager",
						email: item1.email,
						team: "",
						department: item1.department,
						description: "",
						imageUrl: "",
						_companyId: this.viewModel.selected_company()
					});
				}
			});

			// Add employees
			for (let i = 0; i < result.length; i++) {
				if (contains(employee_tree, "id", result[i].employee_id)) {
					// Remove existing entry to avoid duplicates
					employee_tree = employee_tree.filter(item => 
						!(item.id === result[i].employee_id && item.parentId === "-100"));
				}

				employee_tree.push({
					id: result[i].employee_id,
					parentId: "" + result[i].manager_employee_id + "",
					name: result[i].firstname + ' ' + result[i].surname,
					positionName: result[i].position,
					email: result[i].email,
					team: "",
					department: result[i].department,
					description: "",
					imageUrl: "",
					_companyId: this.viewModel.selected_company()
				});
			}

			// Final check if we still have the same company selected
			if (!this.viewModel.selected_company() || this.currentCompanyId !== this.viewModel.selected_company()) {
				return;
			}

			// Ensure the chart container exists and is clean
			let chartContainer = document.querySelector('.chart-container');
			if (!chartContainer) {
				console.error('Chart container not found');
				return;
			}

			// Create the chart with completely fresh instance and data
			chart = new d3org.OrgChart()
				.container('.chart-container')
				.data(employee_tree) // Fresh data reference
				.nodeWidth((d) => 250)
				.initialZoom(0.7)
				.nodeHeight((d) => 175)
				.childrenMargin((d) => 40)
				.compactMarginBetween((d) => 15)
				.compactMarginPair((d) => 80)
				.nodeContent((d, i, arr, state) => {
					// Force fresh company name lookup to prevent stale data
					let companyName = d.data.name;
					if (d.data.id === -100) {
						// Always get the current company name from the view model
						companyName = this.viewModel.company_details().company() || d.data.name;
					}
					
					return `<div style="padding-top:30px;background-color:none;margin-left:1px;height:${
						d.height}px;border-radius:2px;overflow:visible">
						<div style="height:${d.height - 32}px;padding-top:0px;background-color:white;
						border:1px solid lightgray;">
						${d.data.id !== -100 ? `<img src=" ${'/download/employee/' + d.data.id + '/photo'}"
						style="margin-top:-30px;margin-left:${d.width / 2 - 30}px;
						border-radius:100px;width:60px;height:60px;" />` : ''}
						${d.data.id !== -100 ? `<div style="margin-right:10px;margin-top:15px;float:right">${d.data.id}</div>` : ''}
						<div style="margin-top:-30px;background-color:#3AB6E3;height:10px;width:${d.width - 2}px;
						border-radius:1px"></div>
						<div style="padding:20px; padding-top:35px;text-align:center">
							<div style="font-size:16px;font-weight:bold">
							${d.data.id !== -100 ? `<a href="/#/employee/view/${d.data.id}">${companyName}</a>` : companyName}
							</div>
							<div style="color:#404040;font-size:16px;margin-top:4px"> ${d.data.positionName}</div>
						</div>
						<div style="display:flex;justify-content:space-between;padding-left:15px;padding-right:15px;">
							${d.data.id !== -100 ? `<div > Manages:  ${d.data._directSubordinates} 👤</div>` : '' }
							${d.data.id !== -100 ? `<div > Oversees: ${d.data._totalSubordinates} 👤</div>` : '' }
							${d.data.id == -100 ? `<div > Total number of employees: ${d.data._totalSubordinates} 👤</div>` : '' }
						</div>
						</div>
						</div>`;
				});

			// Force a fresh render with new data
			chart.render();

			// Store the chart instance for potential cleanup
			this.chart = chart;

		} catch (err) {
			console.error('Error loading organizational chart:', err);
			// Clear the chart on error and reset selection
			this.clearChart();
			this.viewModel.resetCompany();
			
			Grape.alerts.alert({
				type: 'warning',
				message: tr('The organisational chart does not align with your current company structure.', 
					'The organisational chart does not align with your current company structure.'),
				title: tr('Warning', 'Warning')
			});
		} finally {
			this.isLoading = false;
		}
	}

	async get_company(companyId)
	{
		try {
			if (!companyId) {
				this.viewModel.company_details().company_id(null);
				this.viewModel.company_details().company(null);
				return;
			}

			let selected_company = this.viewModel.companies().find(item => item.company_id === companyId);
			
			if (!selected_company) {
				console.error('Selected company not found in companies list');
				this.viewModel.resetCompany(); // Reset if company not found
				return;
			}

			let selected_name = selected_company.company;
			this.viewModel.company_details().company_id(companyId);
			this.viewModel.company_details().company(selected_name);

			let res = await Grape.fetches.getJSON('/api/record', {
				table: 'v_department_company',
				schema: 'savanna',
				limit: 1000000,
				filter: [{ value: companyId, op: '=', field: 'company_id' }]
			});

			if (res && res.records) {
				for (let i = 0; i < res.records.length; i++) {
					res.records[i].name = res.records[i].department + ' (' + res.records[i].company + ')';
				}
			}
		} catch (error) {
			console.error('Error getting company details:', error);
			// Reset everything on error
			this.viewModel.resetCompany();
		}
	}

	/* Not used currently
	downLoadClicked(v, e)
	{
		let doc = new jsPDF();
		let specialElementHandlers = {'#editor': (element, renderer) => {return true;}};
		$('#cmd').click(() => { doc.fromHTML($('#content').html(), 15, 15, {
			'width': 170,
			'elementHandlers': specialElementHandlers
			});
			doc.save('sample-file.pdf');
		});
	} */
}

export default {
	route: '[/]report/report_company_orgchart',
	page_class: CompanyOrgChartPage,
	template: template,
	template_filename: 'translation/report_company_orgchart'
};